import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, Outlet, useLocation } from 'react-router-dom';
import VoiceCallWidget from '../VoiceCall/VoiceCallWidget';
import AssistantForm from '../Assistant/AssistantForm';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import ResponsiveNavigation from '../common/ResponsiveNavigation';
import { ResponsiveContainer } from '../common/ResponsiveLayout';
import { useMobile } from '../../hooks/useMobile';
import logoImage from '../../assets/images/LifeCoachAssist-logo-03.png';

const { FiLogOut, FiMic, FiUser, FiSettings, FiShield, FiBarChart2 } = FiIcons;

const Dashboard = ({ children }) => {
  const { user, signOut, userRole, loading } = useAuth();
  const location = useLocation();
  const { isMobile, isTablet } = useMobile();

  const [showAssistantForm, setShowAssistantForm] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState(null);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  const handleCreateAssistant = () => {
    setEditingAssistant(null);
    setShowAssistantForm(true);
  };

  const handleEditAssistant = (assistant) => {
    setEditingAssistant(assistant);
    setShowAssistantForm(true);
  };

  const handleSelectAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    toast.success(`Selected assistant: ${assistant.name}`);
  };

 const handleAssistantFormSuccess = () => {
    setShowAssistantForm(false);
    setEditingAssistant(null);
  };

  // Navigation items for responsive navigation
  const navigationItems = [];

  // Only show Contact tab if user is NOT a coach
  if (userRole !== 'COACH' && userRole !== 'coach') {
    navigationItems.push({
      id: 'call',
      label: 'Contact',
      icon: <SafeIcon icon={FiMic} className="w-4 h-4" />,
      path: '/dashboard',
      badge: selectedAssistant ? selectedAssistant.name : null
    });
  }

  // Add Dashboard tab for clients (non-admin, non-coach users)
  if (userRole !== 'SUPER_ADMIN' && userRole !== 'COACH' && userRole !== 'coach') {
    navigationItems.push({
      id: 'my-dashboard',
      label: 'Dashboard',
      icon: <SafeIcon icon={FiBarChart2} className="w-4 h-4" />,
      path: '/my-dashboard'
    });
  }

  // Add admin link if user has permissions
  if (userRole === 'SUPER_ADMIN' || userRole === 'COACH' || userRole === 'coach') {
    navigationItems.push({
      id: 'admin',
      label: 'Admin',
      icon: <SafeIcon icon={FiShield} className="w-4 h-4" />,
      path: '/admin'
    });
  }

  // Logo component
  const logo = (
    <>
      <img src={logoImage} alt="Life Coach Assist" className="w-auto" style={{ height: '3rem' }} />
      <h1 className={`font-bold text-gray-900 ${isMobile ? 'text-lg' : 'text-xl'}`}>
        Life Coach Assist
      </h1>
    </>
  );

  // User info component
  const userInfo = (
    <>
      <SafeIcon icon={FiUser} className="w-4 h-4" />
      <span className="text-sm">{user?.email}</span>
    </>
  );


  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col ${isMobile ? 'pb-safe' : ''}`}>
      {/* Responsive Navigation */}
      <ResponsiveNavigation
        items={navigationItems}
        logo={logo}
        userInfo={userInfo}
        onSignOut={handleSignOut}
        variant="header"
      />

      {/* Main Content */}
      <main className={`flex-grow pt-4 ${isMobile ? 'px-4' : ''}`}>
        <ResponsiveContainer maxWidth="7xl" padding={{ xs: 0, lg: 8 }}>
          <Outlet context={{ handleCreateAssistant, handleEditAssistant, handleSelectAssistant, selectedAssistant, setSelectedAssistant }} />
        </ResponsiveContainer>
      </main>

      {/* Floating Widget - only show when not on call tab and user is not a coach */}
      {location.pathname !== '/dashboard' && userRole !== 'COACH' && userRole !== 'coach' && (
        <VoiceCallWidget
          isFloating={true}
          selectedAssistant={selectedAssistant}
        />
      )}

      {/* Assistant Form Modal */}
      {showAssistantForm && (
        <AssistantForm
          editingAssistant={editingAssistant}
          onSuccess={handleAssistantFormSuccess}
          onCancel={() => setShowAssistantForm(false)}
        />
      )}

      {/* Footer */}
      <footer className="mt-6 pt-1 pb-1 px-4 border-t border-gray-200 bg-white text-center text-sm text-gray-600 pb-safe">
        <p className="text-xs mt-1">&copy; 2025 New Breakthroughs, LLC and Footprints Design, LLC</p>
        <p className="text-xs my-1">
          <a href="https://www.lifecoachassist.ai" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900">
            Trademarked, Copyrighted, Patent Pending
          </a>
        </p>
      </footer>
    </div>
  );
};

export default Dashboard;